import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, Dropdown } from "@/config/antd";
import { ChevronDown } from "lucide-react";
import "@/assets/styles/custom_calender.css";

const Calendar = ({
  isVisible,
  onClose,
  onDateSelect,
  selectedDate: propSelectedDate,
  initialDate,
  markedDates = [], // Array of dates with items to highlight
  onMarkedDateClick, // Callback for when a user clicks a marked date
}) => {
  // Initialize with current date if no initialDate provided
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(
    initialDate?.getMonth() ?? today.getMonth()
  );
  const [currentYear, setCurrentYear] = useState(
    initialDate?.getFullYear() ?? today.getFullYear()
  );
  const [selectedDate, setSelectedDate] = useState(propSelectedDate);
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);

  useEffect(() => {
    setSelectedDate(propSelectedDate);
  }, [propSelectedDate]);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
    (day) => day[0]
  );

  // Generate array of years (from 1990 to current year)
  const years = Array.from(
    { length: today.getFullYear() - 1990 + 1 },
    (_, i) => 1990 + i
  );

  const isDateMarked = (date) => {
    if (!markedDates || !markedDates.length) return false;

    // Use date methods that ignore timezone to compare dates (year, month, day only)
    const dateYear = date.getFullYear();
    const dateMonth = date.getMonth();
    const dateDay = date.getDate();

    return markedDates.some((markedDate) => {
      const marked = new Date(markedDate);

      // Compare year, month, and day directly - ignoring timezone effects
      return (
        marked.getFullYear() === dateYear &&
        marked.getMonth() === dateMonth &&
        marked.getDate() === dateDay
      );
    });
  };

  // Function to get a deterministic color class based on date - matching HomeCalender
  const getColorClass = (date) => {
    // Use date as a seed for consistent color assignment
    const dateNum = parseInt(
      `${date.getFullYear()}${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}${String(date.getDate()).padStart(2, "0")}`,
      10
    );
    const isGreen = dateNum % 2 === 0; // Even dates get green, odd get warning

    return isGreen ? "bg-green text-white" : "bg-warning text-white";
  };

  const getDaysInMonth = (month, year) => {
    const date = new Date(year, month, 1);
    const days = [];
    const firstDay = date.getDay();

    // Add previous month's days
    const prevMonthDays = new Date(year, month, 0).getDate();
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonthDays - i,
        isCurrentMonth: false,
        date: new Date(year, month - 1, prevMonthDays - i),
      });
    }

    // Add current month's days
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        day: i,
        isCurrentMonth: true,
        date: new Date(year, month, i),
      });
    }

    // Add next month's days to complete the grid
    const remainingDays = 42 - days.length; // 6 rows * 7 days = 42
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        day: i,
        isCurrentMonth: false,
        date: new Date(year, month + 1, i),
      });
    }

    return days;
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);

    if (isDateMarked(date) && onMarkedDateClick) {
      // Create a new date using local values to avoid timezone issues
      const newDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        12 // Set noon to avoid any date shifts
      );
      onMarkedDateClick(newDate);
    } else {
      onDateSelect(date);
    }
  };

  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const days = getDaysInMonth(currentMonth, currentYear);

  const monthDropdownItems = {
    items: months.map((month, index) => ({
      key: index,
      label: (
        <button
          className="w-full px-4 py-2 text-left text-sm"
          onClick={() => {
            setCurrentMonth(index);
            setIsMonthDropdownOpen(false);
          }}>
          {month}
        </button>
      ),
    })),
  };

  const yearDropdownItems = {
    items: years.map((year) => ({
      key: year,
      label: (
        <button
          className="w-full px-4 py-2 text-left text-sm"
          onClick={() => {
            setCurrentYear(year);
            setIsYearDropdownOpen(false);
          }}>
          {year}
        </button>
      ),
    })),
  };

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      footer={null}
      width="100%"
      className="custom-calendar-modal"
      closable={false}
      style={{
        maxWidth: "390px",
        margin: "20px auto",
        borderRadius: "10px",
      }}>
      <div className="p-5 bg-white rounded-lg">
        <div className="mb-5">
          <h2 className="heading-medium font-semibold mb-1">Change Date</h2>
          <p className="text-gray-500 text-small">
            Change between Date, Month, and Year
          </p>
        </div>

        <div className="flex items-center justify-between mb-5 px-2">
          <Dropdown
            menu={monthDropdownItems}
            open={isMonthDropdownOpen}
            onOpenChange={setIsMonthDropdownOpen}
            trigger={["click"]}
            dropdownRender={(menu) => (
              <div className="custom-dropdown-menu w-auto max-h-48 overflow-y-auto shadow-md rounded-md">
                {menu}
              </div>
            )}>
            <button
              className={`flex items-center font-medium ${
                isMonthDropdownOpen
                  ? "text-primary font-bold border border-primary rounded-md px-2 py-1"
                  : ""
              }`}>
              {months[currentMonth]}
              <ChevronDown
                className={`ml-2 w-4 h-4 sm:w-5 sm:h-5 transition-transform ${
                  isMonthDropdownOpen ? "rotate-180 text-primary font-bold" : ""
                }`}
              />
            </button>
          </Dropdown>

          <Dropdown
            menu={yearDropdownItems}
            open={isYearDropdownOpen}
            onOpenChange={setIsYearDropdownOpen}
            trigger={["click"]}
            dropdownRender={(menu) => (
              <div className="custom-dropdown-menu w-auto max-h-48 overflow-y-auto shadow-md rounded-md">
                {menu}
              </div>
            )}>
            <button
              className={`flex items-center font-medium ${
                isYearDropdownOpen
                  ? "text-primary font-bold border border-primary rounded-md px-2 py-1"
                  : ""
              }`}>
              {currentYear}
              <ChevronDown
                className={`ml-2 w-4 h-4 sm:w-5 sm:h-5 transition-transform ${
                  isYearDropdownOpen ? "rotate-180 text-primary font-bold" : ""
                }`}
              />
            </button>
          </Dropdown>
        </div>

        <div className="grid grid-cols-7 gap-[6px]">
          {weekDays.map((day, index) => (
            <div
              key={`weekday-${index}`}
              className="flex items-center justify-center text-gray-400 text-small h-8">
              {day}
            </div>
          ))}

          {days.map((day, index) => {
            const isSelected =
              selectedDate &&
              selectedDate.getDate() === day.date.getDate() &&
              selectedDate.getMonth() === day.date.getMonth() &&
              selectedDate.getFullYear() === day.date.getFullYear();

            const todayDate = isToday(day.date);
            const isMarked = isDateMarked(day.date);
            const colorClass = isMarked ? getColorClass(day.date) : "";

            return (
              <button
                key={index}
                onClick={() => handleDateSelect(day.date)}
                className={`
                  aspect-square flex items-center justify-center rounded-lg text-small
                  ${!day.isCurrentMonth ? "text-gray-300" : "text-gray-600"}
                  ${
                    day.isCurrentMonth && !isMarked && !isSelected && !todayDate
                      ? "bg-blue-50"
                      : ""
                  }
                  ${todayDate ? "bg-purple text-white font-medium" : ""}
                  ${isSelected && !todayDate ? "bg-purple-500 text-white" : ""}
                  ${isMarked && !isSelected && !todayDate ? colorClass : ""}
                  ${isMarked && isSelected && !todayDate ? colorClass : ""}
                  ${isMarked ? "cursor-pointer font-medium" : ""}
                  hover:bg-purple-500 hover:text-white transition-colors
                  disabled:cursor-not-allowed
                `}
                style={{ width: "100%", height: "auto" }}>
                {day.day}
              </button>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

Calendar.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  onDateSelect: PropTypes.func,
  selectedDate: PropTypes.instanceOf(Date),
  initialDate: PropTypes.instanceOf(Date),
  markedDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  onMarkedDateClick: PropTypes.func,
};

export default Calendar;
